import { useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import type { TFunction } from 'i18next';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SubTask, SubTaskType } from '../../client/generated/graphql';
import { RitualConfig } from '../../helpers/ritual';
import { CommonIcon } from '../common/CommonIcon';

const ROW_DIVIDER_BORDER = '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)';

interface RitualPageTaskItemProps {
    task: SubTask;
    index: number;
    config: RitualConfig;
    showDivider?: boolean;
    onToggle: (index: number) => void;
    onAction: (index: number) => void;
}

const getTaskTypeIcon = (taskType?: SubTaskType | null): string => {
    switch (taskType) {
        case SubTaskType.Journal:
            return 'description';
        case SubTaskType.Rating:
            return 'star_outline';
        default:
            return '';
    }
};

const getTaskTypeLabel = (taskType: SubTaskType | null | undefined, t: TFunction): string | null => {
    switch (taskType) {
        case SubTaskType.Journal:
            return t('ritual.write');
        case SubTaskType.Rating:
            return t('ritual.rate');
        default:
            return null;
    }
};

export const RitualPageTaskItem: FC<RitualPageTaskItemProps> = ({ task, index, config, showDivider = false, onToggle, onAction }) => {
    const { t } = useTranslation();
    const taskLabel = getTaskTypeLabel(task.taskType, t);
    const taskIcon = getTaskTypeIcon(task.taskType);
    const isSpecialTask = task.taskType === SubTaskType.Journal || task.taskType === SubTaskType.Rating;

    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onToggle(index);
        },
    });

    return (
        <Box
            component="button"
            type="button"
            aria-label={task.done ? t('ritual.markUndone', { name: task.name }) : t('ritual.markDone', { name: task.name })}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            sx={{
                display: 'block',
                width: '100%',
                m: 0,
                px: '16px',
                py: '16px',
                border: 'none',
                borderTop: showDivider ? ROW_DIVIDER_BORDER : 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
                transition: 'background-color 0.12s ease',
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
                <Text
                    style={{
                        fontSize: '18px',
                        fontWeight: 500,
                        textDecoration: task.done ? 'line-through' : 'none',
                        color: task.done ? 'var(--tgui--hint_color)' : 'var(--tg-theme-text-color)',
                    }}
                >
                    {task.name}
                </Text>
                <Stack direction="row" alignItems="center" gap={1.25} sx={{ flexShrink: 0 }}>
                    {isSpecialTask && taskLabel && (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onAction(index);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                padding: '3px 6px',
                                borderRadius: '8px',
                                backgroundColor: config.cardBg,
                                cursor: 'pointer',
                            }}
                        >
                            {taskIcon && (
                                <CommonIcon
                                    icon={taskIcon}
                                    type="outlined"
                                    size={13}
                                    color="white"
                                />
                            )}
                            <Text style={{ fontSize: '11px', fontWeight: 500, color: 'white' }}>
                                {task.done ? t('ritual.editAction') : taskLabel}
                            </Text>
                        </Box>
                    )}
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: `2px solid ${task.done ? config.cardBg : 'var(--tgui--hint_color)'}`,
                            backgroundColor: task.done ? config.cardBg : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {task.done && (
                            <CommonIcon
                                icon="check"
                                type="filled"
                                size={18}
                                color="white"
                            />
                        )}
                    </Box>
                </Stack>
            </Stack>
        </Box>
    );
};
